import "./adjust-credit-balance-modal.scss";
import { observer } from "mobx-react";
import { DefaultModalLayout } from "../../../../../components/modal/layouts/default-modal-layout";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import ControlGroup from "../../../../../components/layouts/control-group/control-group";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import creditsPagedListPageStore from "../../credits-paged-list-page-store";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import { useMemo, useState } from "react";
import { TRANSACTION_TYPES } from "../../../../../services/api/transactions-service";
import { SingleValue } from "react-select";
import { Input } from "../../../../../components/inputs/input/input";
import { BUTTON_VARIANTS } from "../../../../../components/inputs/button-variants";
import { FormField } from "../../../../../components/form-field/form-field";
import FontBody4 from "../../../../../components/fonts/font-body4/font-body4";
import { Textarea } from "../../../../../components/inputs/textarea/textarea";
import ProcessingIndicator from "./processing-indicator/processing-indicator";
import portalStore from "../../../../../stores/current-portal-store";

interface AdjustCreditBalanceModalProps {
    onClose: () => void;
}

const actions: { [key: number]: string; } = {
    [TRANSACTION_TYPES.CREDIT_ADJUSTMENT_ISSUE]: 'Issue credits',
    [TRANSACTION_TYPES.CREDIT_ADJUSTMENT_WITHDRAW]: 'Withdraw credits'
}

function getActionsOptions() {
    const options: any = [];
    for (let i in actions) {
        options.push({ 'value': i, 'label': actions[i] });
    }
    return options;
}

const MAX_AMOUNT_INTEGER_LENGTH = 6;
const MAX_AMOUNT_DECIMAL_LENGTH = 2;
const MAX_COMMENT_LENGTH = 300;

const AdjustCreditBalanceModal: React.FC<AdjustCreditBalanceModalProps> = observer(({ onClose }) => {
    const [action, setAction] = useState<TRANSACTION_TYPES>(TRANSACTION_TYPES.CREDIT_ADJUSTMENT_WITHDRAW);
    const actionValue = action ? { 'value': action, label: actions[action] } : undefined;
    const [adjustAmount, setAdjustAmount] = useState<number>(0);
    const actionOptions = useMemo(() => getActionsOptions(), []);

    const addornment = useMemo(() => {
        if (action === TRANSACTION_TYPES.CREDIT_ADJUSTMENT_ISSUE) {
            return `+${portalStore.currencyCode}`;
        }
        if (action === TRANSACTION_TYPES.CREDIT_ADJUSTMENT_WITHDRAW) {
            return `-${portalStore.currencyCode}`;
        }
        return '';
    }, [action])

    const onChangeToAdjustAmount = (value: number) => {
        if (creditsPagedListPageStore.creditToAdjust?.credits
            && creditsPagedListPageStore.creditToAdjust.credits - value < 0
            && action === TRANSACTION_TYPES.CREDIT_ADJUSTMENT_WITHDRAW) {
            return;
        }

        const amountParts = String(value).split('.');
        const integer = amountParts[0];
        const decimals = amountParts[1];

        if (integer.length > MAX_AMOUNT_INTEGER_LENGTH) {
            return;
        }

        if (decimals && decimals.length > MAX_AMOUNT_DECIMAL_LENGTH) {
            return;
        }

        setAdjustAmount(value);
    }

    const calculatedAjustedBalance = useMemo(() => {
        let amount = creditsPagedListPageStore.creditToAdjust?.credits ?
            Number(creditsPagedListPageStore.creditToAdjust.credits) : 0;

        if (action === TRANSACTION_TYPES.CREDIT_ADJUSTMENT_ISSUE) {
            amount += Number(adjustAmount);
        }

        if (action === TRANSACTION_TYPES.CREDIT_ADJUSTMENT_WITHDRAW) {
            amount -= adjustAmount;
        }

        let formattedAmount = String(amount);

        if (amount % 1 === 0) {
            formattedAmount = amount + '.00';
        } else {
            formattedAmount = amount.toFixed(2);
        }

        return formattedAmount;

    }, [adjustAmount, action]);

    const onSelectActionHandler = (singleValue: SingleValue<IOption>) => {
        if (singleValue) {
            setAction(Number(singleValue.value));
            setAdjustAmount(0);
        }
    }

    const onAdjustHandler = () => {
        if (!action || !comment) {
            return;
        }
        creditsPagedListPageStore.adjustBalance(adjustAmount, action, comment)
    }

    const [comment, setComment] = useState('');

    const onCommentChangeHandler = (value: string) => {
        if (value.length > MAX_COMMENT_LENGTH) {
            return
        }
        setComment(value);
    }

    return (
        <DefaultModalLayout
            isCloseDisabled={false}
            onCloseClicked={onClose}
            buttons={[
                {
                    text: 'Discard',
                    onClick: onClose,
                    variant: BUTTON_VARIANTS.Tertiary
                },
                {
                    text: 'Adjust Balance',
                    onClick: onAdjustHandler,
                    variant: BUTTON_VARIANTS.Primary,
                    disabled: !comment || !adjustAmount || creditsPagedListPageStore.isProcessing
                }
            ]}
        >
            <div className="adjust-credits-balance-modal-content">
                <div className="modal-title">
                    <FontBody1 color={FONT_COLOR.NEUTRAL3}>Adjust credit balance</FontBody1>
                </div>
                <ProcessingIndicator isProcessing={creditsPagedListPageStore.isProcessing} />
                <ControlGroup>
                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Information:</FontBody2>
                    <div className="info-container">
                        <ControlGroup>
                            <FontBody2>Customer Id:</FontBody2>
                            <FontBody2 variant={FONT_VARIANT.BOLD}>
                                {creditsPagedListPageStore.creditToAdjust?.store_customer_id}
                            </FontBody2>
                        </ControlGroup>
                        <ControlGroup>
                            <FontBody2>Balance:</FontBody2>
                            <FontBody2 variant={FONT_VARIANT.BOLD}>
                                {portalStore.currencyCode} {creditsPagedListPageStore.creditToAdjust?.credits}
                            </FontBody2>
                        </ControlGroup>
                    </div>
                </ControlGroup>

                <ControlGroup>
                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Action:</FontBody2>
                    <SelectDropdown
                        value={actionValue}
                        options={actionOptions}
                        onChange={onSelectActionHandler}
                        placeholder='Please Select'
                    />
                </ControlGroup>

                <ControlGroup>
                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Amount:</FontBody2>
                    <div className="adjust-amount-container">
                        <Input
                            adornment={addornment}
                            min={0}
                            value={adjustAmount}
                            onChange={onChangeToAdjustAmount}
                            type={'number'}
                            placeholder="Enter amount"
                        />
                        <ControlGroup>
                            <FontBody2 color={FONT_COLOR.PRIMARY2}>Adjusted Balance:</FontBody2>
                            <FontBody2 variant={FONT_VARIANT.BOLD}>
                                {portalStore.currencyCode} {calculatedAjustedBalance}
                            </FontBody2>
                        </ControlGroup>
                    </div>

                </ControlGroup>
                <ControlGroup>
                    <FormField label='Comment' required>
                        <FontBody4>
                            Explain why you adjust the customer’s store credit balance.<br />
                            Customers will be able to see the note on their transactions page.
                        </FontBody4>
                    </FormField>
                    <div className="comment-container">
                        <Textarea
                            value={comment}
                            onChange={onCommentChangeHandler}
                            placeholder="Example: Adjusted per customer request"
                        />
                        <FontBody4>
                            {comment.length} of {MAX_COMMENT_LENGTH}
                        </FontBody4>
                    </div>
                </ControlGroup>
            </div>
        </DefaultModalLayout>
    );
});

export default AdjustCreditBalanceModal;
